/**
 * Command Palette action registry — Sprint A3 (22 Feb 2026).
 *
 * Context-aware acties die verschijnen in de Command Palette op basis van
 * de huidige sectie en/of geselecteerde entity. Uitbreidbaar via
 * `registerContextActions(...)` — nieuwe modules registreren hun eigen
 * acties zonder deze file te wijzigen.
 *
 * Ook AI-READY: elke action heeft een optioneel `ai_intent` veld dat de
 * toekomstige AI-laag kan gebruiken om natuurlijke-taal-commando's
 * ("open de laatste factuur van Jan Jansen") naar een action te mappen.
 *
 * Contract:
 *   {
 *     key: string,             // stabiele id
 *     label: string,           // UI-label ("Verstuur herinnering")
 *     description?: string,    // subtitle
 *     icon: string,            // lucide-react icon-naam
 *     section?: string,        // huidige nav-sectie waar deze action bij past
 *     entity_type?: string,    // of: gekoppeld aan geopende entity (customer, invoice, booking, ...)
 *     shortcut?: string[],     // keyboard-shortcut zoals ["mod", "n"]
 *     permission?: string,     // vereiste permissie
 *     ai_intent?: string,      // vrije-tekst hint voor AI ("send-payment-reminder")
 *     action: (ctx) => void,   // wat te doen bij Enter/klik — ctx = {activeKey, entityId, navigate, ...}
 *   }
 */

const _CONTEXT_ACTIONS = [];
const _SLASH_ROUTES = [];

/**
 * Registreer één of meer context-actions.
 */
export function registerContextActions(actions) {
  const list = Array.isArray(actions) ? actions : [actions];
  list.forEach((a) => {
    if (!a.key || !a.label || !a.icon || !a.action) {
      throw new Error(`registerContextActions: action mist verplichte velden (${JSON.stringify(a)})`);
    }
    _CONTEXT_ACTIONS.push(a);
  });
}

/**
 * Filter acties op basis van huidige context.
 *   activeSection: bv "klanten", "facturen", "boekingen"
 *   entityType:    bv "customer", "invoice", "booking" (als een entity is geopend)
 *   hasPermission: functie (perm) => boolean
 *
 * Voorbeelden:
 *   getContextActions({activeSection: "klanten"}) → global klant-acties
 *   getContextActions({activeSection: "klanten", entityType: "customer"}) → klant-specifieke acties
 */
export function getContextActions({ activeSection, entityType, hasPermission = () => true }) {
  return _CONTEXT_ACTIONS.filter((a) => {
    if (a.permission && !hasPermission(a.permission)) return false;
    // Match als: geen section-constraint OF section matches OR entity_type matches
    const sectionMatch = !a.section || a.section === activeSection;
    const entityMatch = !a.entity_type || a.entity_type === entityType;
    return sectionMatch && entityMatch;
  });
}

/**
 * Slash-command router: /customer → filter naar customer-entity-search
 * Nieuwe modules registreren hun slash-cmd via `registerSlashRoute`.
 */
export function registerSlashRoute(command, entityType, label) {
  _SLASH_ROUTES.push({
    command: command.replace(/^\//, "").toLowerCase(),
    entityType,
    label,
  });
}

export function resolveSlashCommand(input) {
  const m = (input || "").match(/^\/([a-z_]+)(?:\s+(.*))?$/i);
  if (!m) return null;
  const cmd = m[1].toLowerCase();
  const rest = m[2] || "";
  const route = _SLASH_ROUTES.find((r) => r.command === cmd);
  if (!route) return null;
  return { entityType: route.entityType, label: route.label, remainingQuery: rest };
}

export function getAllSlashRoutes() {
  return _SLASH_ROUTES.slice();
}

/**
 * Bootstrap — registreer de standaard 5999Cargo context-acties en
 * slash-commands. Nieuwe modules doen dit vanuit hun eigen bootstrap.
 */
export function bootstrapDefaultCommandRegistry({ navigateTo } = {}) {
  // Reset registry (idempotent voor hot-reload + tests)
  _CONTEXT_ACTIONS.length = 0;
  _SLASH_ROUTES.length = 0;

  const nav = navigateTo || ((section, hash) => {
    const url = `/admin?section=${section}${hash ? `#${hash}` : ""}`;
    window.location.assign(url);
  });

  // ─── SLASH COMMANDS — één regel per entity-type ─────────────
  const slashConfigs = [
    ["customer", "customer", "Klanten"],
    ["contact",  "contact",  "Contactpersonen"],
    ["supplier", "supplier", "Leveranciers"],
    ["invoice",  "invoice",  "Facturen"],
    ["quote",    "quote",    "Offertes"],
    ["booking",  "booking",  "Boekingen"],
    ["pickup",   "pickup",   "Pickups"],
    ["delivery", "delivery", "Deliveries"],
    ["shipment", "shipment", "Zendingen"],
    ["warehouse","warehouse_item", "Warehouse Items"],
    ["container","container","Containers"],
    ["flight",   "flight",   "Vluchten"],
    ["report",   "report",   "Rapportages"],
    ["employee", "employee", "Medewerkers"],
    ["ticket",   "ticket",   "Tickets"],
    ["asset",    "asset",    "Assets"],
  ];
  slashConfigs.forEach(([cmd, et, label]) => registerSlashRoute(cmd, et, label));

  // ─── GLOBAL QUICK ACTIONS — altijd zichtbaar ───────────────
  registerContextActions([
    {
      key: "global:new_customer", label: "Nieuwe klant", icon: "UserPlus",
      description: "Maak een nieuwe klant aan",
      shortcut: ["mod", "shift", "c"],
      permission: "customers:view",
      ai_intent: "create-customer",
      action: () => nav("klanten", "nieuw"),
    },
    {
      key: "global:new_invoice", label: "Nieuwe factuur", icon: "Receipt",
      description: "Maak een nieuwe factuur aan",
      shortcut: ["mod", "shift", "i"],
      permission: "quotes:view",
      ai_intent: "create-invoice",
      action: () => nav("facturen", "nieuw"),
    },
    {
      key: "global:new_quote", label: "Nieuwe offerte", icon: "FilePlus",
      permission: "quotes:view",
      ai_intent: "create-quote",
      action: () => nav("offertes", "nieuw"),
    },
    {
      key: "global:new_booking", label: "Nieuwe boeking", icon: "Package",
      permission: "customers:view",
      ai_intent: "create-booking",
      action: () => nav("documenten", "nieuw-boeking"),
    },
    {
      key: "global:new_pickup", label: "Nieuwe pickup", icon: "Truck",
      permission: "pickup:view",
      ai_intent: "create-pickup",
      action: () => nav("pickup", "nieuw"),
    },
    {
      key: "global:new_delivery", label: "Nieuwe delivery", icon: "MapPin",
      permission: "pickup:view",
      ai_intent: "create-delivery",
      action: () => nav("pickup", "nieuw-delivery"),
    },
    {
      key: "global:new_warehouse", label: "Nieuw warehouse-item", icon: "PackagePlus",
      permission: "quotes:view",
      ai_intent: "create-warehouse-item",
      action: () => nav("warehouse", "nieuw"),
    },
    {
      key: "global:new_container", label: "Nieuwe container", icon: "Container",
      permission: "consolidations:view",
      ai_intent: "create-container",
      action: () => nav("consolidations", "nieuw"),
    },
    {
      key: "global:new_flight", label: "Nieuwe vlucht", icon: "PlaneTakeoff",
      permission: "consolidations:view",
      ai_intent: "create-flight",
      action: () => nav("consolidations", "vlucht-nieuw"),
    },
    {
      key: "global:new_communication", label: "Nieuwe communicatie", icon: "MessageSquare",
      permission: "quotes:view",
      ai_intent: "create-communication",
      action: () => nav("communicatie", "nieuw"),
    },
    {
      key: "global:new_payment", label: "Nieuwe betaling registreren", icon: "CreditCard",
      permission: "quotes:view",
      ai_intent: "register-payment",
      action: () => nav("facturen", "nieuw-betaling"),
    },
  ]);

  // ─── CUSTOMER CONTEXT ACTIONS ──────────────────────────────
  registerContextActions([
    {
      key: "customer:new_invoice", label: "Nieuwe factuur voor deze klant",
      icon: "Receipt", entity_type: "customer",
      permission: "quotes:view",
      ai_intent: "create-invoice-for-customer",
      action: (ctx) => nav("facturen", `nieuw-voor-klant-${ctx.entityId}`),
    },
    {
      key: "customer:new_quote", label: "Nieuwe offerte voor deze klant",
      icon: "FilePlus", entity_type: "customer",
      permission: "quotes:view",
      action: (ctx) => nav("offertes", `nieuw-voor-klant-${ctx.entityId}`),
    },
    {
      key: "customer:new_booking", label: "Nieuwe boeking voor deze klant",
      icon: "Package", entity_type: "customer",
      permission: "customers:view",
      action: (ctx) => nav("documenten", `nieuw-boeking-klant-${ctx.entityId}`),
    },
    {
      key: "customer:new_pickup", label: "Nieuwe pickup voor deze klant",
      icon: "Truck", entity_type: "customer",
      permission: "pickup:view",
      action: (ctx) => nav("pickup", `nieuw-klant-${ctx.entityId}`),
    },
    {
      key: "customer:new_delivery", label: "Nieuwe delivery voor deze klant",
      icon: "MapPin", entity_type: "customer",
      permission: "pickup:view",
      action: (ctx) => nav("pickup", `nieuw-delivery-klant-${ctx.entityId}`),
    },
    {
      key: "customer:new_warehouse", label: "Nieuwe warehouse-opdracht",
      icon: "Warehouse", entity_type: "customer",
      permission: "quotes:view",
      action: (ctx) => nav("warehouse", `nieuw-klant-${ctx.entityId}`),
    },
    {
      key: "customer:new_communication", label: "Nieuwe communicatie",
      icon: "Send", entity_type: "customer",
      permission: "quotes:view",
      action: (ctx) => nav("communicatie", `nieuw-klant-${ctx.entityId}`),
    },
    {
      key: "customer:open_portal", label: "Open klantportaal",
      icon: "ExternalLink", entity_type: "customer",
      ai_intent: "open-customer-portal",
      action: (ctx) => window.open(`/portaal?klant=${ctx.entityId}`, "_blank"),
    },
  ]);

  // ─── INVOICE CONTEXT ACTIONS ───────────────────────────────
  registerContextActions([
    {
      key: "invoice:send_reminder", label: "Verstuur herinnering",
      icon: "Bell", entity_type: "invoice",
      permission: "reminders:view",
      ai_intent: "send-payment-reminder",
      action: (ctx) => nav("facturen", `herinnering-${ctx.entityId}`),
    },
    {
      key: "invoice:stripe_checkout", label: "Stripe Checkout openen",
      icon: "CreditCard", entity_type: "invoice",
      permission: "quotes:view",
      action: (ctx) => nav("facturen", `checkout-${ctx.entityId}`),
    },
    {
      key: "invoice:register_payment", label: "Betaling registreren",
      icon: "CheckCircle2", entity_type: "invoice",
      permission: "quotes:view",
      action: (ctx) => nav("facturen", `betaling-${ctx.entityId}`),
    },
    {
      key: "invoice:credit_note", label: "Creditnota maken",
      icon: "FileMinus", entity_type: "invoice",
      permission: "quotes:view",
      action: (ctx) => nav("facturen", `creditnota-${ctx.entityId}`),
    },
    {
      key: "invoice:open_communication", label: "Communicatie openen",
      icon: "MessageSquare", entity_type: "invoice",
      permission: "quotes:view",
      action: (ctx) => nav("communicatie", `factuur-${ctx.entityId}`),
    },
    {
      key: "invoice:download_pdf", label: "PDF downloaden",
      icon: "Download", entity_type: "invoice",
      permission: "quotes:view",
      action: (ctx) => window.open(`/api/admin/sales-invoices/${ctx.entityId}/pdf`, "_blank"),
    },
  ]);

  // ─── BOOKING CONTEXT ACTIONS ───────────────────────────────
  registerContextActions([
    {
      key: "booking:plan_pickup", label: "Pickup plannen",
      icon: "Truck", entity_type: "booking",
      permission: "pickup:view",
      action: (ctx) => nav("pickup", `nieuw-boeking-${ctx.entityId}`),
    },
    {
      key: "booking:plan_delivery", label: "Delivery plannen",
      icon: "MapPin", entity_type: "booking",
      permission: "pickup:view",
      action: (ctx) => nav("pickup", `nieuw-delivery-boeking-${ctx.entityId}`),
    },
    {
      key: "booking:open_warehouse", label: "Warehouse openen",
      icon: "Warehouse", entity_type: "booking",
      permission: "quotes:view",
      action: (ctx) => nav("warehouse", `boeking-${ctx.entityId}`),
    },
    {
      key: "booking:prepare_manifest", label: "Manifest voorbereiden",
      icon: "Ship", entity_type: "booking",
      permission: "bookings:view",
      action: (ctx) => nav("documenten", `manifest-${ctx.entityId}`),
    },
    {
      key: "booking:open_tracking", label: "Tracking openen",
      icon: "MapPinned", entity_type: "booking",
      permission: "quotes:view",
      action: (ctx) => nav("tracking_labels", `boeking-${ctx.entityId}`),
    },
    {
      key: "booking:link_flight", label: "Flight koppelen",
      icon: "PlaneTakeoff", entity_type: "booking",
      permission: "consolidations:view",
      action: (ctx) => nav("consolidations", `koppel-boeking-${ctx.entityId}`),
    },
  ]);

  // ─── WAREHOUSE ITEM CONTEXT ACTIONS ────────────────────────
  registerContextActions([
    {
      key: "warehouse:move", label: "Verplaatsen",
      icon: "MoveRight", entity_type: "warehouse_item",
      permission: "quotes:view",
      action: (ctx) => nav("warehouse", `verplaats-${ctx.entityId}`),
    },
    {
      key: "warehouse:print_label", label: "Label printen",
      icon: "Printer", entity_type: "warehouse_item",
      permission: "quotes:view",
      action: (ctx) => window.open(`/api/admin/warehouse-items/${ctx.entityId}/label`, "_blank"),
    },
    {
      key: "warehouse:open_shipment", label: "Shipment openen",
      icon: "Truck", entity_type: "warehouse_item",
      permission: "quotes:view",
      action: (ctx) => nav("tracking_labels", `warehouse-${ctx.entityId}`),
    },
    {
      key: "warehouse:add_photo", label: "Foto toevoegen",
      icon: "Camera", entity_type: "warehouse_item",
      permission: "quotes:view",
      action: (ctx) => nav("warehouse", `foto-${ctx.entityId}`),
    },
  ]);

  // ─── HELP ACTIONS (always visible) ─────────────────────────
  registerContextActions([
    {
      key: "help:shortcuts", label: "Sneltoetsen tonen",
      description: "Overzicht van keyboard shortcuts",
      icon: "Keyboard",
      section: "__help__",
      ai_intent: "show-keyboard-shortcuts",
      action: () => window.dispatchEvent(new CustomEvent("cargo:show-shortcuts")),
    },
    {
      key: "help:docs", label: "Documentatie openen",
      description: "Open de 5999Cargo documentatie",
      icon: "BookOpen",
      section: "__help__",
      action: () => window.open("/admin?section=documenten", "_blank"),
    },
    {
      key: "help:support", label: "Contact support",
      description: "Neem contact op met 5999Cargo support",
      icon: "LifeBuoy",
      section: "__help__",
      action: () => window.open("mailto:support@5999cargo.nl", "_blank"),
    },
  ]);
}

// Auto-bootstrap voor eerste gebruik. Kan opnieuw worden aangeroepen door
// tests of hot-reload.
bootstrapDefaultCommandRegistry();
